import React, { useState, useEffect } from "react";
import { GeneTherapeuticHeader } from "../Header/GeneTherapeuticHeader";
import { DiseaseSelect } from "../DiseaseSelect/DiseaseSelect";
import { BubblePlot } from "../BubblePlot/BubblePlot";
import { expressionProfileClient } from "../../services/expressionProfileClient";
import { TractabilityHeatmap } from "../TractabilityHeatmap/TractabilityHeatmap";
import type { DiseaseOption } from "../../services/diseaseOptionsClient";

interface ExpressionProfileProps {
  selectedGene?: string;
  therapeuticArea?: string;
  taxonomyNodes?: import("../../types/diseaseTaxonomy.types").DiseaseTaxonomyNode[];
  selectedDiseaseIds?: Set<number>;
  onDiseaseSelectionChange?: (ids: Set<number>) => void;
}

export const ExpressionProfile: React.FC<ExpressionProfileProps> = ({
  selectedGene = "",
  therapeuticArea = "",
  taxonomyNodes,
  selectedDiseaseIds,
  onDiseaseSelectionChange,
}) => {
  const [selectedPlotType] = useState<string>("bubble");
  const [selectedDisease, setSelectedDisease] = useState<string>("");
  const [diseaseData, setDiseaseData] = useState<any[]>([]);
  const [heatmapData, setHeatmapData] = useState<any[]>([]);
  const [diseaseOptions] = useState<DiseaseOption[]>([]);

  // Get the display label for the selected disease
  const getSelectedDiseaseLabel = () => {
    const found = diseaseOptions.find(
      (d: DiseaseOption) => d.id === selectedDisease,
    );
    return found?.label || selectedDisease.replace(/_/g, " ");
  };

  useEffect(() => {
    if (!selectedDisease) {
      return;
    }
    expressionProfileClient
      .getDiseaseData(selectedDisease)
      .then(setDiseaseData);
  }, [selectedDisease]);

  useEffect(() => {
    // Process diseaseData to generate heatmapData
    const newHeatmapData: any[] = [];
    diseaseData.forEach((item: any) => {
      const {
        Gene: _Gene,
        gene: _gene,
        ensembl_id: _ensembl_id,
        Goal_end_FDR: _Goal_end_FDR,
        Goal_end_vs_random_pval: _Goal_end_vs_random_pval,
        N_Detections: _N_Detections,
        Shift_to_goal_end: _Shift_to_goal_end,
        Sig: _Sig,
        ...rest
      } = item;
      newHeatmapData.push(rest);
    });
    setHeatmapData(newHeatmapData);
  }, [diseaseData]);

  return (
    <div className="w-full space-y-8">
      {/* <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900 rounded-xl p-6 shadow-sm">
        <GeneTherapeuticHeader
          selectedGene={selectedGene}
          therapeuticArea={therapeuticArea ?? undefined}
        />
      </div> */}

      <GeneTherapeuticHeader
        selectedGene={selectedGene}
        therapeuticArea={therapeuticArea ?? undefined}
        taxonomyNodes={taxonomyNodes}
        selectedDiseaseIds={selectedDiseaseIds}
        onDiseaseSelectionChange={onDiseaseSelectionChange}
      />

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <DiseaseSelect
          therapeuticArea={therapeuticArea ?? ""}
          selectedDisease={selectedDisease}
          onDiseaseChange={(diseaseId: string) => setSelectedDisease(diseaseId)}
          geneId={selectedGene || undefined}
        />
      </div>

      {selectedDisease && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Enhanced header with title */}
          <div className="bg-gray-50 dark:bg-gray-900 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  Expression Analysis
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Dataset: {getSelectedDiseaseLabel()}
                </p>
              </div>
            </div>
          </div>

          {/* Enhanced Content area for selected tab */}
          <div className="p-6">
            <div className="h-[650px] flex flex-col">
              {selectedPlotType === "bubble" ? (
                diseaseData.length === 0 ? (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 dark:border-blue-400 mx-auto mb-4"></div>
                      <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        Loading Bubble Plot
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Fetching expression data...
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                      <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                        Geneformer In-Silico Perturbation Analysis
                      </h4>
                      <div className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                        <p className="leading-relaxed">
                          This plot shows results from{" "}
                          <strong>Geneformer</strong>, a context-aware,
                          attention-based deep learning model, pretrained on
                          large-scale transcriptomic data to enable predictions
                          in settings with limited data. It predicts how
                          silencing each gene would shift the cellular state
                          from disease toward a healthy reference.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                          <div className="bg-white dark:bg-gray-800 p-3 rounded border border-gray-200 dark:border-gray-700">
                            <span className="font-semibold text-blue-600 dark:text-blue-400">
                              Shift_to_goal_end
                            </span>
                            <p className="text-xs mt-1">
                              Cosine similarity shift from disease state toward
                              healthy reference state. Higher values = stronger
                              therapeutic potential.
                            </p>
                          </div>
                          <div className="bg-white dark:bg-gray-800 p-3 rounded border border-gray-200 dark:border-gray-700">
                            <span className="font-semibold text-blue-600 dark:text-blue-400">
                              N_Detections
                            </span>
                            <p className="text-xs mt-1">
                              Number of disease cells where the gene was
                              detected as expressed.
                            </p>
                          </div>
                          <div className="bg-white dark:bg-gray-800 p-3 rounded border border-gray-200 dark:border-gray-700">
                            <span className="font-semibold text-blue-600 dark:text-blue-400">
                              FDR
                            </span>
                            <p className="text-xs mt-1">
                              Benjamini–Hochberg corrected p-value from Wilcoxon
                              test comparing perturbation effect to random
                              background.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex-1">
                      <BubblePlot
                        data={diseaseData}
                        highlightedGene={selectedGene}
                      />
                    </div>
                  </>
                )
              ) : diseaseData.length === 0 ? (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 dark:border-blue-400 mx-auto mb-4"></div>
                    <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      Loading Heatmap
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Processing tractability data...
                    </p>
                  </div>
                </div>
              ) : (
                <div className="h-full overflow-hidden">
                  <TractabilityHeatmap
                    data={heatmapData}
                    highlightGene={selectedGene}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
